import { base64UrlToUint8Array, uint8ArrayToBase64Url } from 'utils/base64Url';
import { getDomainName } from 'utils/domain';
import { jsonStringify } from 'utils/json';
import { getIsNativePlatform } from 'utils/platform';

/**
 * Whether the current environment supports WebAuthn / passkeys (the `PublicKeyCredential` API).
 * Single source of truth for the passkey-gating predicate used by the login tab, the enrollment
 * prompt host, and the account-settings section (DEV-3881).
 */
export const isWebAuthnSupported = () =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential;

/**
 * The relying-party id (rpId) for a passkey ceremony.
 *
 * On the web this is the page host (`window.location.hostname`). Inside the native Capacitor shell
 * the SPA runs from `capacitor://localhost`, so the host is `localhost` — useless as an rpId; use the
 * brand's real domain (seeded into `lsDomainName` by the mobile build) instead. That matches both the
 * Associated-Domain the app is bound to and the `https://<domain>` origin the native WebAuthn shim
 * reports to the server (DEV-4084).
 */
export const getPasskeyRpId = () =>
  getIsNativePlatform() ? getDomainName() : window.location.hostname;

/**
 * Discriminant passed to `buildPublicKeyOptions` / `serializeCredentialResponse`
 * to select the correct ceremony-specific fields.
 *
 * - GET  — `navigator.credentials.get()`  — passkey assertion (sign-in)
 * - CREATE — `navigator.credentials.create()` — passkey registration (enroll)
 */
export const WEBAUTHN_KIND = /** @type {const} */ ({
  GET: 'get',
  CREATE: 'create',
});

/**
 * Builds the `publicKey` argument for `navigator.credentials.get()` (assertion)
 * or `navigator.credentials.create()` (registration) from the server-supplied options JSON.
 *
 * Shared transformations for both ceremonies:
 *   - `challenge` decoded via base64UrlToUint8Array (Fido2NetLib emits base64url).
 *   - Credential-descriptor list ids decoded (allowCredentials for GET; excludeCredentials for CREATE).
 *   - Empty RP ID replaced with `window.location.hostname`:
 *       GET: top-level `rpId` field — an explicit empty string throws SecurityError in navigator.credentials.get().
 *       CREATE: nested `rp.id` field — same SecurityError contract in navigator.credentials.create().
 *
 * Ceremony-specific (CREATE only):
 *   - `user.id` decoded via base64UrlToUint8Array (the user handle is a binary opaque id).
 *
 * @param {object} options - The raw server options JSON (Fido2NetLib `ToJson()` contract).
 * @param {'get' | 'create'} kind - Which ceremony — use the WEBAUTHN_KIND constant.
 * @returns {object} The publicKey options object ready for the browser WebAuthn API.
 */
export const buildPublicKeyOptions = (options, kind) => {
  const challenge = base64UrlToUint8Array(options.challenge);

  if (kind === WEBAUTHN_KIND.GET) {
    return {
      ...options,
      // Backend leaves rpId empty (ServerDomain unset) to mean "use this origin"; an explicit empty rpId
      // makes navigator.credentials.get() throw SecurityError, so fall back to the rpId for this surface
      // (page host on web; the brand domain in the native shell — DEV-3881 / DEV-4084).
      rpId: options.rpId || getPasskeyRpId(),
      challenge,
      allowCredentials: (options.allowCredentials || []).map(c => ({
        ...c,
        id: base64UrlToUint8Array(c.id),
      })),
    };
  }

  // kind === WEBAUTHN_KIND.CREATE
  return {
    ...options,
    // The backend leaves rp.id empty (Fido2Configuration.ServerDomain unset) to mean "use this origin".
    // navigator.credentials.create() rejects an EXPLICIT empty rp.id with a SecurityError, so fall back to
    // this surface's rpId (page host on web; the brand domain in the native shell) — which also matches the
    // rpId we send to the backend on completion below.
    rp: { ...options.rp, id: options.rp?.id || getPasskeyRpId() },
    challenge,
    user: {
      ...options.user,
      id: base64UrlToUint8Array(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials || []).map(credentialDescriptor => ({
      ...credentialDescriptor,
      id: base64UrlToUint8Array(credentialDescriptor.id),
    })),
  };
};

/**
 * Serializes a WebAuthn `PublicKeyCredential` response to the JSON string expected by the backend
 * (Fido2NetLib's `AuthenticatorAssertionRawResponse` / `AuthenticatorAttestationRawResponse`).
 *
 * Both ceremonies share the outer envelope:
 *   { id, rawId (base64url), type, response: { clientDataJSON (base64url), ... } }
 *
 * GET (assertion / sign-in) response fields:
 *   - authenticatorData (base64url)
 *   - clientDataJSON (base64url)
 *   - signature (base64url)
 *   - userHandle (base64url or null) — REQUIRED for a discoverable (usernameless) sign-in;
 *     the backend rejects the assertion without it (DEV-3881). Null-guarded: null when absent.
 *
 * CREATE (attestation / enroll) response fields:
 *   - attestationObject (base64url)
 *   - clientDataJSON (base64url)
 *
 * @param {PublicKeyCredential} credential - The credential returned by the browser WebAuthn API.
 * @param {'get' | 'create'} kind - Which ceremony — use the WEBAUTHN_KIND constant.
 * @returns {string} JSON-stringified credential envelope.
 */
export const serializeCredentialResponse = (credential, kind) => {
  const base = {
    id: credential.id,
    rawId: uint8ArrayToBase64Url(credential.rawId),
    type: credential.type,
  };

  if (kind === WEBAUTHN_KIND.GET) {
    return jsonStringify({
      ...base,
      response: {
        authenticatorData: uint8ArrayToBase64Url(credential.response.authenticatorData),
        clientDataJSON: uint8ArrayToBase64Url(credential.response.clientDataJSON),
        signature: uint8ArrayToBase64Url(credential.response.signature),
        // userHandle identifies the resident credential's user — REQUIRED for a discoverable
        // (usernameless) sign-in; the backend rejects the assertion without it (DEV-3881).
        userHandle: credential.response.userHandle
          ? uint8ArrayToBase64Url(credential.response.userHandle)
          : null,
      },
    });
  }

  // kind === WEBAUTHN_KIND.CREATE
  return jsonStringify({
    ...base,
    response: {
      attestationObject: uint8ArrayToBase64Url(credential.response.attestationObject),
      clientDataJSON: uint8ArrayToBase64Url(credential.response.clientDataJSON),
    },
  });
};

// iOS surfaces passkey-ceremony failures as ASAuthorizationError NSErrors. The @capgo/capacitor-passkey
// plugin forwards them as a generic DOMException whose `message` carries the raw native string (e.g.
// "The operation couldn't be completed. (com.apple.AuthenticationServices.AuthorizationError error 1006.)")
// but NOT the WebAuthn `name` the web ceremony would set. So a robust classifier must read the numeric
// ASAuthorizationError code out of the message, not only the DOMException name (DEV-4084).
const APPLE_AUTH_ERROR = {
  CANCELED: 1001, // ASAuthorizationError.canceled — user dismissed the system sheet
  MATCHED_EXCLUDED_CREDENTIAL: 1006, // iOS 18+ — a credential already on this device is in excludeCredentials
};

const getAppleAuthErrorCode = error => {
  const match = String(error?.message || '').match(/AuthorizationError error (\d+)/);

  return match ? Number(match[1]) : null;
};

/**
 * Whether a passkey-ceremony error means "this device already has a passkey for this account" — the
 * EXPECTED duplicate case the authenticator refuses. True for the web `InvalidStateError` AND the iOS
 * native `matchedExcludedCredential` (ASAuthorizationError 1006). Callers render this NEUTRALLY (it is
 * not a failure) and should not report it as an exception.
 */
export const isPasskeyAlreadyEnrolledError = error =>
  error?.name === 'InvalidStateError' ||
  getAppleAuthErrorCode(error) === APPLE_AUTH_ERROR.MATCHED_EXCLUDED_CREDENTIAL;

/**
 * Whether a passkey-ceremony error means the user canceled or the sheet timed out — the web
 * `NotAllowedError` AND the iOS native canceled (ASAuthorizationError 1001). Also an expected,
 * non-exception case.
 */
export const isPasskeyCanceledError = error =>
  error?.name === 'NotAllowedError' || getAppleAuthErrorCode(error) === APPLE_AUTH_ERROR.CANCELED;
